import React from "react";
import {
  CommonCodeGroupType,
  useGetCommonCodeGroupsByTypeAsAdminQuery,
} from "../../../lib/apollo/graphql/generated";
import BasicButton from "../../common/components/BasicButton";
import BoxWithShadow from "../../common/components/BoxWithShadow";
import Checkbox from "../../common/components/Checkbox";
import Loader from "../../common/components/Loader";
import SelectBox from "../../common/components/SelectBox";
import { WelfareFilters } from "../container/WelfareRegisterContainer";

const WELFARE_COMMON_CODES = [
  CommonCodeGroupType.WelfareContentCategory,
  CommonCodeGroupType.WelfareContentFamilyType,
  CommonCodeGroupType.WelfareContentLifeCycle,
  CommonCodeGroupType.WelfareContentType,
] as const;

type WelfareCommonCodeType = (typeof WELFARE_COMMON_CODES)[number];
type ActiveCode = {
  id: string;
  name: string;
  code: string;
};

const checkIsWelfareCodeGroup = (
  groupCode?: string
): groupCode is WelfareCommonCodeType => {
  if (
    !groupCode ||
    !WELFARE_COMMON_CODES.includes(groupCode as WelfareCommonCodeType)
  ) {
    return false;
  }

  return true;
};

interface CheckboxGridProps {
  title: string;
  children: React.ReactNode;
}

function CheckboxGrid({ title, children }: CheckboxGridProps) {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-lg font-bold">{title}</p>
      <ul className="grid grid-cols-2 gap-1">{children}</ul>
    </div>
  );
}

interface BokjiroSearchOptionsProps {
  filters: WelfareFilters;
  onSearch: () => void;
  onChange: (
    key: keyof WelfareFilters,
    value: WelfareFilters[keyof WelfareFilters]
  ) => void;
}

export default function BokjiroSearchOptions({
  filters,
  onChange,
  onSearch,
}: BokjiroSearchOptionsProps) {
  const { data, loading } = useGetCommonCodeGroupsByTypeAsAdminQuery({
    variables: {
      types: [...WELFARE_COMMON_CODES],
    },
  });

  if (loading) {
    return (
      <BoxWithShadow className="w-1/4 flex-shrink-0">
        <p className="text-lg font-bold">복지로 검색</p>
        <Loader />
      </BoxWithShadow>
    );
  }

  const codes = data?.getCommonCodeGroupsByTypeAsAdmin || [];
  const grouped: {
    [key in WelfareCommonCodeType]?: ActiveCode[];
  } = {};

  for (const code of codes) {
    if (!checkIsWelfareCodeGroup(code.groupCode)) {
      continue;
    }

    grouped[code.groupCode] = [...(code.activeCommonCodes || [])];
  }

  const centralGovtId =
    grouped[CommonCodeGroupType.WelfareContentType]?.find(
      (item) => item.name === "중앙부처"
    )?.id || "";

  const energyId =
    grouped[CommonCodeGroupType.WelfareContentCategory]?.find(
      (item) => item.name === "에너지"
    )?.id || "";

  return (
    <BoxWithShadow className="w-1/4 flex-shrink-0 gap-3">
      <div className="flex h-full flex-col gap-5 overflow-y-auto">
        <div className="flex flex-col gap-1">
          <p className="text-lg font-bold">행정 구분</p>
          <SelectBox
            value={filters.welfareTypeId}
            onSelect={(typeId) => {
              if (centralGovtId !== typeId) {
                onChange(
                  "welfareCategoryIds",
                  filters.welfareCategoryIds.filter((code) => code !== energyId)
                );
              }

              onChange("welfareTypeId", typeId);
            }}
            options={(
              grouped[CommonCodeGroupType.WelfareContentType] || []
            ).map((code) => ({
              label: code.name || "",
              value: code.id || "",
            }))}
          />
        </div>

        {filters.welfareTypeId && (
          <>
            <CheckboxGrid title="관심주제">
              {grouped[CommonCodeGroupType.WelfareContentCategory]?.map(
                (code) => {
                  if (
                    centralGovtId !== filters.welfareTypeId &&
                    code.name === "에너지"
                  ) {
                    return null;
                  }

                  const { welfareCategoryIds: categoryIds } = filters;
                  const isChecked = categoryIds.includes(code.id);

                  return (
                    <Checkbox
                      label={code.name}
                      key={code.id}
                      isChecked={isChecked}
                      onCheck={(status) => {
                        if (!status && isChecked) {
                          onChange(
                            "welfareCategoryIds",
                            categoryIds.filter((item) => item !== code.id)
                          );
                        } else {
                          onChange("welfareCategoryIds", [
                            ...categoryIds,
                            code.id,
                          ]);
                        }
                      }}
                    />
                  );
                }
              )}
            </CheckboxGrid>

            <CheckboxGrid title="생애주기">
              {grouped[CommonCodeGroupType.WelfareContentLifeCycle]?.map(
                (code) => {
                  const { welfareLifeCycleIds: lifecycleIds } = filters;
                  const isChecked = lifecycleIds.includes(code.code);

                  return (
                    <Checkbox
                      label={code.name}
                      key={code.id}
                      isChecked={isChecked}
                      onCheck={(status) => {
                        if (!status && isChecked) {
                          onChange(
                            "welfareLifeCycleIds",
                            lifecycleIds.filter((item) => item !== code.code)
                          );
                        } else {
                          onChange("welfareLifeCycleIds", [
                            ...lifecycleIds,
                            code.code,
                          ]);
                        }
                      }}
                    />
                  );
                }
              )}
            </CheckboxGrid>

            <CheckboxGrid title="가구상황">
              {grouped[CommonCodeGroupType.WelfareContentFamilyType]?.map(
                (code) => {
                  const { welfareFamilyTypeIds: familyTypeIds } = filters;
                  const isChecked = familyTypeIds.includes(code.code);

                  return (
                    <Checkbox
                      label={code.name}
                      key={code.id}
                      isChecked={isChecked}
                      onCheck={(status) => {
                        if (!status && isChecked) {
                          onChange(
                            "welfareFamilyTypeIds",
                            familyTypeIds.filter((item) => item !== code.code)
                          );
                        } else {
                          onChange("welfareFamilyTypeIds", [
                            ...familyTypeIds,
                            code.code,
                          ]);
                        }
                      }}
                    />
                  );
                }
              )}
            </CheckboxGrid>
          </>
        )}
      </div>
      <BasicButton
        text="복지로 검색"
        className="w-full"
        onClick={onSearch}
        disabled={!filters.welfareTypeId}
      />
    </BoxWithShadow>
  );
}
